/**
 * Adapter — the single coupling point between transport and UI blocks.
 *
 * Today: converts AI SDK tool part data into UIBlockEnvelopes.
 * If the backend changes (framework swap, data parts, etc.),
 * only this file needs to be rewritten.
 */

import type { UIMessage } from 'ai';
import type { UIBlockEnvelope, UIBlockType, UserAnswerPart } from './blocks/types.js';
import {
  isUserAnswerPart,
  isValidActionListBlock,
  isValidChartBlock,
  isValidDataTableBlock,
  isValidEntityCardBlock,
  isValidKPIBlock,
  isValidQuestionBlock,
  isValidTimelineBlock,
} from './blocks/types.js';

/** Maps transport-specific tool names to semantic block types. */
const TOOL_TO_BLOCK: Record<string, UIBlockType> = {
  show_table: 'table',
  show_kpi: 'kpi',
  show_entity_card: 'entity_card',
  show_chart: 'chart',
  show_timeline: 'timeline',
  show_action_list: 'action_list',
  ask_user: 'question',
};

/** Internal tools that should not render as UI blocks. */
const INTERNAL_TOOLS = new Set(['queryData']);

/** Validators per block type — Issue 4.0.4 */
const BLOCK_VALIDATORS: Record<UIBlockType, (data: unknown) => boolean> = {
  table: isValidDataTableBlock,
  kpi: isValidKPIBlock,
  entity_card: isValidEntityCardBlock,
  chart: isValidChartBlock,
  timeline: isValidTimelineBlock,
  action_list: isValidActionListBlock,
  question: isValidQuestionBlock,
};

/**
 * Detect Mastra tool validation errors returned as the tool output.
 * These have { error: true, message: "Tool input validation failed..." }.
 */
function isToolError(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return d.error === true && typeof d.message === 'string';
}

/**
 * Try to unwrap data that may be nested by the transport layer.
 * Handles: raw object, { result: ... }, JSON string.
 */
function unwrapData(data: unknown): unknown {
  if (data == null) return data;

  // If it's a string, try to JSON parse it
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }

  // If the transport wrapped the result in a { result: ... } envelope, unwrap it
  if (typeof data === 'object' && 'result' in (data as Record<string, unknown>)) {
    const inner = (data as Record<string, unknown>).result;
    if (inner && typeof inner === 'object') return inner;
  }

  return data;
}

/**
 * Convert transport-specific tool data into a UIBlockEnvelope.
 * Returns null for text parts, internal tools, or unknown tools.
 *
 * @param toolName - The tool name from the AI SDK part
 * @param state - The tool part state (input-streaming, input-available, output-available, output-error)
 * @param data - The tool output (when available) or input (when streaming)
 * @param toolCallId - The AI SDK tool call id, used to scope answer lookups for interactive blocks
 */
export function partToBlockEnvelope(
  toolName: string,
  state: string,
  data?: unknown,
  toolCallId?: string
): UIBlockEnvelope | null {
  if (INTERNAL_TOOLS.has(toolName)) return null;

  const blockType = TOOL_TO_BLOCK[toolName];
  if (!blockType) return null;

  // Loading states
  if (state === 'input-streaming' || state === 'input-available') {
    return { type: blockType, loading: true, partialData: data, toolCallId };
  }

  // Error state
  if (state === 'output-error') {
    return { type: 'error', message: String(data ?? 'Component failed to render') };
  }

  // Loaded state — unwrap and validate before producing envelope
  if (state === 'output-available') {
    // Display-tool failures (tool-validation errors OR data-shape mismatches)
    // are hidden from the user: the assistant's text response is the primary
    // answer, and a half-rendered/"Something went wrong" card just adds noise.
    // The backend still receives the error as a tool_result (so the agent can
    // retry) and fires a Slack alert for the dev team, so we don't lose
    // observability.
    if (isToolError(data)) {
      const msg = (data as Record<string, unknown>).message;
      console.warn(`[generative-ui] ${blockType} tool error (suppressed):`, msg);
      return null;
    }

    const unwrapped = unwrapData(data);
    const validator = BLOCK_VALIDATORS[blockType];
    if (validator && !validator(unwrapped)) {
      console.warn(`[generative-ui] ${blockType} validation failed (suppressed). Received:`, data);
      return null;
    }
    return { type: blockType, loading: false, data: unwrapped, toolCallId };
  }

  return null;
}

/**
 * Scan messages after `assistantMessageId` for a `data-user-answer` part
 * matching `toolCallId`. Returns the chosen `answerId` if valid, else null.
 *
 * This is how we recover a prior answer to an `ask_user` block after the
 * user leaves and reopens a thread — the answer persists as a structured
 * UIMessage part on the user's follow-up message, round-tripping through
 * the backend's CommanderGoMessage.metadata JSONB column.
 */
export function findPriorAnswer(
  messages: UIMessage[],
  assistantMessageId: string,
  toolCallId: string,
  validAnswerIds: Set<string>
): string | null {
  const startIdx = messages.findIndex((m) => m.id === assistantMessageId);
  if (startIdx === -1) return null;
  for (let i = startIdx + 1; i < messages.length; i++) {
    const parts = messages[i]?.parts ?? [];
    for (const part of parts) {
      if (!isUserAnswerPart(part)) continue;
      const answer = (part as UserAnswerPart).data;
      if (answer.toolCallId !== toolCallId) continue;
      return validAnswerIds.has(answer.answerId) ? answer.answerId : null;
    }
  }
  return null;
}
